import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, CheckCircle2, AlertCircle, Lock, RefreshCw, CreditCard, QrCode } from 'lucide-react';
import { PixPending } from './PixCheckout';
import { PayeeHeader } from './PayeeHeader';
import { CouponField } from './CouponField';
import { maskAs } from '../lib/autoMask';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BRL = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const errText = (e, f) => (typeof e.response?.data?.detail === 'string' ? e.response.data.detail : f);
const digits = (v) => v.replace(/\D/g, '');
const EMPTY = { holder_name: '', number: '', expiry: '', ccv: '', cpf_cnpj: '', email: '', postal_code: '', address_number: '', phone: '' };
const input = { width: '100%', padding: '0.55rem 0.65rem', borderRadius: 8, border: '1px solid #cbd5e0', fontSize: '0.92rem', background: 'white' };
const payBtn = (busy) => ({ background: '#2f855a', color: 'white', border: 'none', borderRadius: 8, padding: '0.7rem', fontWeight: 700, cursor: busy ? 'wait' : 'pointer' });

const Field = ({ label, name, value, onChange, half, ...rest }) => (
  <label style={{ display: 'block', fontSize: '0.78rem', color: '#4a5568', gridColumn: half ? 'auto' : '1 / -1' }}>
    {label}
    <input name={name} value={value} onChange={onChange} data-testid={`card-${name}`} style={{ ...input, marginTop: 3 }} {...rest} />
  </label>
);

const Result = ({ ok, text, onDone, onRetry, color, doneLabel = 'Começar a ler' }) => (
  <div data-testid="payment-result" style={{ textAlign: 'center', padding: '1rem 0' }}>
    {ok ? <CheckCircle2 className="w-10 h-10" style={{ color: '#38a169', margin: '0 auto 0.5rem' }} />
      : <AlertCircle className="w-10 h-10" style={{ color: '#d69e2e', margin: '0 auto 0.5rem' }} />}
    <p data-testid="payment-result-message" style={{ fontWeight: 600, color: '#2D3748', marginBottom: '1rem' }}>{text}</p>
    <button type="button" onClick={ok ? onDone : onRetry} data-testid={ok ? 'payment-continue-btn' : 'payment-retry-btn'}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: color, color: 'white', border: 'none', borderRadius: 8, padding: '0.6rem 1.25rem', fontWeight: 600, cursor: 'pointer' }}>
      {ok ? doneLabel : <><RefreshCw className="w-4 h-4" />Tentar novamente</>}
    </button>
  </div>
);

export const CardForm = ({ monthly, busy, onPay, f, setF, label }) => {
  const set = (e) => setF((s) => ({ ...s, [e.target.name]: e.target.value }));
  const submit = (e) => {
    e.preventDefault();
    const [mm, yy] = f.expiry.split('/').map((x) => digits(x || ''));
    onPay({ ...f, number: digits(f.number), expiry_month: mm || '', expiry_year: yy || '', expiry: undefined });
    setF((s) => ({ ...s, number: '', ccv: '', expiry: '' }));
  };
  return (
    <form onSubmit={submit} data-testid="card-form" autoComplete="on" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>
      <Field label="Número do cartão" name="number" value={f.number} onChange={set} inputMode="numeric" autoComplete="cc-number" placeholder="0000 0000 0000 0000" required />
      <Field label="Nome impresso no cartão" name="holder_name" value={f.holder_name} onChange={set} autoComplete="cc-name" required />
      <Field half label="Validade (MM/AA)" name="expiry" value={f.expiry} onChange={set} autoComplete="cc-exp" placeholder="12/30" required />
      <Field half label="CVV" name="ccv" value={f.ccv} onChange={set} inputMode="numeric" autoComplete="cc-csc" maxLength={4} required />
      <Field half label="CPF/CNPJ do titular" name="cpf_cnpj" value={f.cpf_cnpj} onChange={set} inputMode="numeric" required />
      <Field half label="Celular do titular" name="phone" value={f.phone} onChange={set} inputMode="tel" autoComplete="tel" placeholder="(11) 99999-9999" required />
      <Field label="E-mail" name="email" value={f.email} onChange={set} type="email" autoComplete="email" required />
      <Field half label="CEP" name="postal_code" value={f.postal_code} onChange={set} inputMode="numeric" autoComplete="postal-code" required />
      <Field half label="Número do endereço" name="address_number" value={f.address_number} onChange={set} required />
      <button type="submit" disabled={busy} data-testid="card-pay-btn" style={{ ...payBtn(busy), gridColumn: '1 / -1' }}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" style={{ margin: '0 auto' }} /> : (label || `Assinar por ${BRL(monthly)}/mês`)}
      </button>
    </form>
  );
};

const PixForm = ({ total, busy, onPix, p, setP }) => {
  const set = (e) => setP((s) => ({ ...s, [e.target.name.replace('pix-', '')]: e.target.value }));
  return (
    <form onSubmit={(e) => { e.preventDefault(); onPix(p); }} data-testid="pix-form" style={{ display: 'grid', gap: '0.6rem' }}>
      <label style={{ fontSize: '0.78rem', color: '#4a5568' }}>Quantos meses deseja pagar?
        <select value={p.meses} onChange={(e) => setP((s) => ({ ...s, meses: Number(e.target.value) }))} data-testid="pix-months-select"
          style={{ display: 'block', width: '100%', marginTop: 4, padding: '0.55rem', border: '1px solid #e2e8f0', borderRadius: 8, background: 'white', color: '#2D3748' }}>
          {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => <option key={m} value={m}>{m} {m === 1 ? 'mês' : 'meses'}</option>)}
        </select>
      </label>
      <Field label="CPF/CNPJ do pagador" name="pix-cpf" value={p.cpf} onChange={set} inputMode="numeric" required />
      <Field label="E-mail" name="pix-email" value={p.email} onChange={set} type="email" autoComplete="email" required />
      <button type="submit" disabled={busy} data-testid="pix-generate-btn" style={payBtn(busy)}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" style={{ margin: '0 auto' }} /> : `Gerar Pix de ${BRL(total)}`}
      </button>
    </form>
  );
};

const PixNote = ({ amount }) => (
  <p data-testid="pix-subscription-note" style={{ fontSize: '0.82rem', color: '#4a5568', marginBottom: '0.75rem' }}>
    Mensalidade de {BRL(amount)} no Pix. Escolha de 1 a 12 meses e pague tudo de uma vez. Ao fim do período pago, enviamos por e-mail o novo Pix (avisos 5 dias e 1 dia antes do vencimento).
  </p>
);

// Card = monthly automatic subscription; Pix = monthly charge e-mailed until cancelled. Card data is cleared after every attempt and never stored
export const AsaasCheckout = ({ editorId, token, amount, payerEmail, color = '#68AAB0', onDone, onPaid, cardOnly = false, initialTab = 'card' }) => {
  const [form, setForm] = useState({ ...EMPTY, email: payerEmail || '' });
  const [pixPayer, setPixPayer] = useState({ cpf: '', email: payerEmail || '', meses: 1 });
  const [method, setMethod] = useState(initialTab === 'pix' ? 'pix' : 'card');
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState(null);
  const [coupon, setCoupon] = useState(null);
  const [activeCoupon, setActiveCoupon] = useState(null);
  const base = activeCoupon ? activeCoupon.final : amount;
  const isPix = method === 'pix' && !cardOnly;
  const monthly = coupon ? coupon.final : Math.round(Number(base || 0) * 100) / 100;
  const total = Math.round(monthly * (pixPayer.meses || 1) * 100) / 100;
  const headers = { Authorization: `Bearer ${token}` };
  useEffect(() => {
    axios.get(`${API}/reader/payment-config/${editorId}`, { headers: { Authorization: `Bearer ${token}` } }).then(({ data }) => {
      setActiveCoupon(data.active_coupon || null);
      const cpf = maskAs('cpfCnpj', data.payer_cpf || '');
      const email = data.payer_email || payerEmail || '';
      setForm((s) => ({ ...s, cpf_cnpj: s.cpf_cnpj || cpf, email: s.email || email, phone: s.phone || maskAs('phone', data.payer_phone || '') }));
      setPixPayer((s) => ({ ...s, cpf: s.cpf || cpf, email: s.email || email }));
    }).catch(() => {});
  }, [editorId, token, payerEmail]);
  const run = async (fn) => {
    setBusy(true);
    try { await fn(); } catch (e) { setView({ kind: 'result', ok: false, text: errText(e, 'Não foi possível concluir. Tente novamente.') }); } finally { setBusy(false); }
  };
  const payCard = (data) => run(async () => {
    const { data: r } = await axios.post(`${API}/reader/subscription/card/${editorId}`, { ...data, coupon: cardOnly ? undefined : coupon?.code }, { headers });
    if (r.active) onPaid?.();
    setView(r.active || !r.payment_id ? { kind: 'result', ok: r.active, text: r.message } : { kind: 'pending', pid: r.payment_id, text: r.message });
  });
  const payPix = (p) => run(async () => {
    const { data: r } = await axios.post(`${API}/reader/pix/${editorId}`, { meses: p.meses || 1, identification_number: p.cpf, payer_email: p.email, coupon: coupon?.code }, { headers });
    setView({ kind: 'pix', pix: r });
  });
  const retry = () => setView(null);

  if (view?.kind === 'pix') return <PixPending editorId={editorId} token={token} pix={view.pix} color={color} onDone={onDone} onPaid={onPaid} onExpired={retry} onBack={retry} />;
  if (view?.kind === 'pending') return <PixPending editorId={editorId} token={token} pix={{ payment_id: view.pid, pending_card: true, message: view.text }} color={color} onDone={onDone} onPaid={onPaid} onExpired={retry} />;
  if (view?.kind === 'result') return <Result ok={view.ok} text={view.text} onDone={onDone} onRetry={retry} color={color} doneLabel={cardOnly ? 'Concluir' : undefined} />;
  const tab = (m, Icon, label) => (
    <button type="button" onClick={() => setMethod(m)} data-testid={`pay-tab-${m}`}
      style={{ flex: 1, display: 'inline-flex', justifyContent: 'center', alignItems: 'center', gap: 6, padding: '0.55rem', borderRadius: 8, fontWeight: 600, cursor: 'pointer',
        border: `1px solid ${method === m ? color : '#e2e8f0'}`, background: method === m ? `${color}14` : 'white', color: '#2D3748' }}>
      <Icon className="w-4 h-4" />{label}
    </button>
  );
  return (
    <div data-testid="asaas-checkout">
      <PayeeHeader />
      {!cardOnly && <div style={{ display: 'flex', gap: 8, marginBottom: '0.9rem' }}>{tab('card', CreditCard, 'Cartão')}{tab('pix', QrCode, 'Pix')}</div>}
      {isPix ? <PixNote amount={coupon ? coupon.final : base} /> : !cardOnly && (
        <p data-testid="card-subscription-note" style={{ fontSize: '0.82rem', color: '#4a5568', marginBottom: '0.75rem' }}>
          Cobrança automática de {BRL(coupon ? coupon.final : base)} todo mês no cartão. Cancele quando quiser em Minha assinatura.
        </p>
      )}
      {activeCoupon && <p data-testid="active-coupon-note" style={{ fontSize: '0.8rem', color: '#2f855a', fontWeight: 600, marginBottom: '0.75rem' }}>Cupom {activeCoupon.code} ({activeCoupon.percent}%) aplicado em todas as mensalidades enquanto sua assinatura estiver ativa.</p>}
      {!cardOnly && !activeCoupon && <CouponField checkUrl={`${API}/reader/coupon/${editorId}`} headers={headers} onChange={setCoupon} note={`em todas as mensalidades: ${BRL(coupon?.final)}/mês enquanto a assinatura estiver ativa`} />}
      {isPix ? <PixForm total={total} busy={busy} onPix={payPix} p={pixPayer} setP={setPixPayer} /> : <CardForm monthly={coupon ? coupon.final : base} busy={busy} onPay={payCard} f={form} setF={setForm} label={cardOnly ? 'Salvar novo cartão' : undefined} />}
      <p data-testid="payment-footnote" style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.75rem', color: '#718096', marginTop: '0.6rem' }}>
        <Lock className="w-3 h-3" /> {isPix ? 'Cancele quando quiser em Minha assinatura: as cobranças Pix param e o acesso segue até o fim do período pago.' : 'Processado pelo Asaas. Os dados do cartão não ficam salvos no Livrum.'}
      </p>
    </div>
  );
};
